import {useCallback, useRef, useEffect} from 'react';
import _ from 'lodash';
import {qw, SEC} from '../util.js';
import {useLiveRef, useObjectMemo} from './react_hooks.js';

/**
 * @typedef {Object} WebSocketOptions
 * @property {string} url
 * @property {boolean} enable
 * @property {number} retry
 * @property {function} onOpen
 * @property {function} onError
 * @property {function} onClose
 * @property {function} onMessage
 * @property {Object} actionMap 
 */

/**
 * @typedef {Object} WebSocketHook
 * @property {function} sendMessage
 * @property {function} sendJSONMessage
 * @property {function} sendEvent
 */

export const ReadyState = {
    CONNECTING: 0,
    OPEN: 1,
    CLOSING: 2,
    CLOSED: 3,
};

export const CloseCode = {
    OK: 1000,
    GOING_AWAY: 1001,
    PROTOCOL_ERROR: 1002,
    UNSUPPORTED_DATA: 1003,
    NO_STATUS: 1005,
    ABNORMAL: 1006,
    INVALID_FRAME_PAYLOAD_DATA: 1007,
    POLICY_VIOLATION: 1008,
    MESSAGE_TOO_BIG: 1009,
    MANDATORY_EXTENSTION: 1010,
    INTERNAL_ERROR: 1011,
    SERVICE_RESTART: 1012,
    TRY_AGAIN_LATER: 1013,
    BAD_GATEWAY: 1014,
    TLS_HANDSHAKE: 1015,
    UNAUTHORIZED: 3000,
    FORBIDDEN: 3003,
    TIMEOUT: 3008,
};

const OPT_KEYS = qw`retry onOpen onError onClose onMessage actionMap`;

const RETRY_INTERVAL = 3*SEC;

/**
 * @param {WebSocketOptions} opt Options
 * @returns {WebSocketHook} Hook
 */
export const useWebSocket = (opt={})=>{
    let {
        url,
        enable=true,
    } = opt;
    let wsRef = useRef(null);
    let messageQueueRef = useRef([]);
    let optMemo = useObjectMemo(_.pick(opt, OPT_KEYS));
    let optRef = useLiveRef(optMemo);
    let retryCountRef = useRef(0);
    let sendMessage = useCallback(data=>{
        if (wsRef.current?.readyState===ReadyState.OPEN)
            return void wsRef.current.send(data);
        messageQueueRef.current.push(data);
    }, []);
    let sendJSONMessage = useCallback(data=>sendMessage(JSON.stringify(data)), [sendMessage]);
    let sendEvent = useCallback((action, data)=>sendJSONMessage({action, data}), [sendJSONMessage]);
    let wsHook = useObjectMemo({
        sendMessage,
        sendJSONMessage,
        sendEvent,
    });
    let wsHookRef = useLiveRef(wsHook);
    useEffect(()=>{
        if (!url||!enable)
            return;
        let {
            retry=3,
            onOpen,
            onError,
            onClose,
            onMessage,
            actionMap,
        } = optRef.current;
        let close = ()=>{
            if (!wsRef.current)
                return;
            if ([ReadyState.CONNECTING, ReadyState.OPEN].includes(wsRef.current.readyState))
                wsRef.current.close();
            wsRef.current = null;
        };
        let create = ()=>{
            let doRetry = ()=>{
                if (retry===true) {
                    setTimeout(()=>create(), RETRY_INTERVAL);
                    return true;
                }
                if (!retry||retryCountRef.current>=retry)
                    return false;
                retryCountRef.current++;
                setTimeout(()=>create(), RETRY_INTERVAL);
                return true;
            }
            wsRef.current = new WebSocket(url);
            wsRef.current.onopen = async ()=>{
                retryCountRef.current = 0;
                await onOpen?.(wsHookRef.current);
                while (messageQueueRef.current.length)
                    wsHookRef.current.sendMessage(messageQueueRef.current.shift());
            };
            wsRef.current.onmessage = ev=>{
                let {data: rawData} = ev;
                if (actionMap) {
                    let jsonData;
                    try { jsonData = JSON.parse(rawData); } catch {}
                    if (!_.isArray(jsonData)) {
                        let {action, data} = jsonData||{};
                        actionMap[action]?.(wsHookRef.current, data);
                    }
                }
                onMessage?.(rawData, wsHookRef.current);
            };
            wsRef.current.onerror = async ev=>{
                if (!doRetry())
                    await onError?.(ev);
            };
            wsRef.current.onclose = async ev=>{
                if (ev.code===CloseCode.ABNORMAL) {
                    if (doRetry())
                        return;
                }
                await onClose?.(ev);
                close();
            };
        };
        create();
        return ()=>close();
    }, [url, enable]);
    return useObjectMemo({sendMessage, sendJSONMessage, sendEvent});
};
